import {
  DUNGEON,
  NIGHTMARES,
  SIGN_CLASH,
  SIMA,
  SIMA_ANY_ERA,
  SIMA_BANDS,
  SIMA_CREATURE,
  SIMA_HAZARD,
  SIMA_SCALE,
  SIMA_SIGN,
  SIMA_SIGNS,
  STRATA,
  STRATUM,
  WARD_TYPES,
  simaScale,
  type DamageType,
  type EnemyKind,
  type NightmareType,
  type SimaBand,
  type SimaCreature,
  type SimaHazard,
  type SimaSign,
  type StratumId,
} from '@somnhold/content';
import { touchGrid } from '../grid.ts';
import { hexDist, hexLen, hexRing, hexToWorld, type Hex } from '../hex.ts';
import { createDungeonMap } from '../map.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng, type RngState } from '../math/rng.ts';
import type { BattleState, DungeonLayout, DungeonRoom, DungeonSpawn, DungeonState, Enemy, Hero, MapDef, SimaRun, Zone } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { drawPuzzles, makePuzzle } from './deep.ts';
import { placeCryptTraps } from './ascuas.ts';

/*
 * The Chasm (la Sima, docs/SIMA.md): a crypt with no known bottom. One floor at a time: each is laid in one of two
 * slots either side of the map's centre (the map never grows), from the week's order of the twelve strata, with its
 * stratum's hazards, bestiary and guardian; every fifth floor a guardian's hall instead of a captain's. Going down
 * lays the next floor in the other slot and drops the one above. Every number comes from `SIMA` and its tables.
 */

const FOREVER = 1_000_000_000;
const W = (h: Hex): { x: number; y: number } => hexToWorld(h.q, h.r);
const standing = (h: Hero): boolean => h.downTicks === 0;

/** The map every Chasm run has: two slots, each wide enough for a floor's chain of rooms. */
export const SIMA_RADIUS = SIMA.slotSpan * 2 + 2;
const slotCentre = (slot: number): Hex => ({
  q: slot === 0 ? -SIMA.slotSpan : SIMA.slotSpan,
  r: 0,
});

/** An empty Chasm map (the first floor is laid when the battle starts). */
export const simaMap = (): MapDef => ({
  radius: SIMA_RADIUS,
  bastion: 0,
  sectors: [],
  kind: 'dungeon',
  dungeon: {
    rooms: [{ q: -SIMA.slotSpan, r: 0, radius: 2, kind: 'entry' }],
    doors: [],
    floor: [],
  },
});

/** What a floor is, from the week alone (the card shows it before anyone goes down). */
export interface SimaFloorInfo {
  stratum: StratumId;
  band: SimaBand;
  signs: SimaSign[];
  ward?: DamageType;
  threshold: boolean;
}

/** The band of a floor: ten floors each, the abyss from the fortieth on. */
export const bandOf = (floor: number): SimaBand => SIMA_BANDS[imin(SIMA_BANDS.length - 1, idiv(floor, SIMA.band))] as SimaBand;

/** The week's order of the twelve strata: a shuffle from its seed (a floor's stratum is `order[(floor − 1) mod 12]`). */
export const simaOrder = (weekSeed: number): number[] => {
  const rng = seedRng(deriveSeed(weekSeed, 77));
  const o = STRATA.map((_, i) => i);
  for (let i = o.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    [o[i], o[j]] = [o[j] as number, o[i] as number];
  }
  return o;
};

/** A floor of the week: its stratum, band, signs (and the type a ward names), and whether a guardian waits. */
export const simaFloor = (weekSeed: number, order: number[], floor: number): SimaFloorInfo => {
  const stratum = STRATA[order[(floor - 1) % order.length] ?? 0] as StratumId;
  const band = bandOf(floor);
  const n = imin(SIMA.maxSigns, idiv(floor, SIMA.band));
  const rng = seedRng(deriveSeed(weekSeed, 1000 + floor));
  const signs: SimaSign[] = [];
  if (band === 'abyss') signs.push(SIMA.abyssSign);
  let guard = 0;
  while (signs.length < n && guard++ < 40) {
    const g = SIMA_SIGNS[rngInt(rng, SIMA_SIGNS.length)] as SimaSign;
    if (signs.includes(g)) continue;
    if (SIGN_CLASH.some(([a, b]) => (a === g && signs.includes(b)) || (b === g && signs.includes(a)))) continue;
    signs.push(g);
  }
  const ward = signs.includes('ward') ? WARD_TYPES[rngInt(rng, WARD_TYPES.length)] : undefined;
  return {
    stratum,
    band,
    signs,
    ...(ward ? { ward } : {}),
    threshold: floor % SIMA.threshold === 0,
  };
};

/** Health and blows on a floor (bp). */
export const simaHpBp = (floor: number): number => simaScale(SIMA_SCALE.hp, floor);
export const simaDmgBp = (floor: number): number => simaScale(SIMA_SCALE.dmg, floor);

/**
 * A floor's chain of rooms (entrance, fighting room, hazard room, hall) laid in its slot: drawn until every cell fits
 * the slot (each try a seed of its own, so the same floor always comes out the same).
 */
const fitChain = (seed: number, slot: number): DungeonLayout => {
  const c = slotCentre(slot);
  let best: DungeonLayout | null = null;
  for (let k = 0; k < 24; k++) {
    const L = createDungeonMap(deriveSeed(seed, 300 + k), 2, [3, 4]).dungeon as DungeonLayout;
    // The chain's middle on the slot's centre.
    let sq = 0;
    let sr = 0;
    for (const h of L.floor) {
      sq += h.q;
      sr += h.r;
    }
    const mq = idiv(sq, L.floor.length || 1);
    const mr = idiv(sr, L.floor.length || 1);
    const dq = c.q - mq;
    const dr = c.r - mr;
    const moved: DungeonLayout = {
      rooms: L.rooms.map((r) => ({ ...r, q: r.q + dq, r: r.r + dr })),
      doors: L.doors.map((h) => ({ q: h.q + dq, r: h.r + dr })),
      floor: L.floor.map((h) => ({ q: h.q + dq, r: h.r + dr })),
    };
    best = moved;
    if (moved.floor.every((h) => hexDist(h, c) <= SIMA.slotSpan - 1)) return moved;
  }
  return best as DungeonLayout;
};

/** Cells of a room, inside its rim (not its centre). */
const roomCells = (room: DungeonRoom, rim = 1): Hex[] => {
  const out: Hex[] = [];
  for (let k = 1; k <= room.radius - rim; k++) for (const h of hexRing(k)) out.push({ q: room.q + h.q, r: room.r + h.r });
  return out;
};

/** Points of a nightmare in a wave's budget (the Chasm's own creatures as their stats say). */
const pointsOf = (k: NightmareType | SimaCreature): number => imax(1, NIGHTMARES[k].points);

/** The weights this stratum gives, cut to what the group's era allows (never empty: the stratum's own creature or a skeleton). */
const bestiaryOf = (sm: SimaRun, stratum: StratumId): [NightmareType | SimaCreature, number][] => {
  const b = STRATUM[stratum].bestiary;
  const out = (Object.keys(b) as (NightmareType | SimaCreature)[])
    .sort()
    .filter((k) => sm.allowed.includes(k) || SIMA_ANY_ERA.includes(k))
    .map((k) => [k, b[k] as number] as [NightmareType | SimaCreature, number]);
  return out.length ? out : [['skeleton', 1]];
};

/** The hall's captain: the stratum's big one, or the era's. */
const CAPTAIN: Record<StratumId, EnemyKind[]> = {
  ossuary: ['ogre'],
  marrow: ['troll', 'ogre'],
  glass: ['ogre'],
  cisterns: ['troll', 'ogre'],
  buried: ['rootwight'],
  grotto: ['troll', 'ogre'],
  frost: ['ogre'],
  geode: ['beetle', 'ogre'],
  archive: ['bonesinger', 'ogre'],
  forge: ['molten'],
  nest: ['weaver'],
  ribs: ['troll', 'ogre'],
};
export const captainOf = (sm: SimaRun, stratum: StratumId): EnemyKind =>
  CAPTAIN[stratum].find((k) => SIMA_ANY_ERA.includes(k as SimaCreature) || sm.allowed.includes(k as NightmareType)) ?? 'skeleton';

/** The waves of the fighting room and the hazard room, filled to the party and the floor's band. */
const floorSpawns = (sm: SimaRun, L: DungeonLayout, rng: RngState): DungeonSpawn[] => {
  const spawns: DungeonSpawn[] = [];
  const entries = bestiaryOf(sm, sm.stratum);
  const ink = SIMA.inkBp[SIMA_BANDS.indexOf(sm.band)] ?? 0;
  for (const i of [1, 2]) {
    const room = L.rooms[i];
    const door = L.doors[i - 1];
    if (!room || !door) continue;
    const far = roomCells(room).filter((h) => hexDist(h, door) >= 3);
    const pts = SIMA.roomPoints[i - 1] as number;
    for (let w = 0; w < DUNGEON.wavesPerRoom; w++) {
      let left = mulBp(pts, sm.countBp);
      let guard = 0;
      while (left > 0 && guard++ < 80) {
        const fit = entries.filter(([k]) => pointsOf(k) <= left);
        if (!fit.length) break;
        const kind = rngWeighted(rng, fit);
        left -= pointsOf(kind);
        const at = far[rngInt(rng, far.length)] as Hex;
        spawns.push({
          room: i,
          wave: w,
          kind,
          tier: rngChance(rng, ink) ? 2 : 1,
          elite: false,
          q: at.q,
          r: at.r,
        });
      }
      // The procession: a mourner more in every wave.
      if (sm.signs.includes('procession'))
        for (let k = 0; k < SIMA_SIGN.procession.mourners; k++) {
          const at = far[rngInt(rng, far.length)] as Hex;
          spawns.push({
            room: i,
            wave: w,
            kind: 'mourner',
            tier: 1,
            elite: false,
            q: at.q,
            r: at.r,
          });
        }
    }
    // Elites at the end of the fighting room: more the bigger the party and the deeper the band.
    if (i === 1) {
      const n = idiv(sm.party - 1, 2) + SIMA_BANDS.indexOf(sm.band);
      const pair = sm.signs.includes('pair') ? 2 : 1;
      for (let e = 0; e < n * pair; e++) {
        const at = far[rngInt(rng, far.length)] as Hex;
        const kind = entries[e % entries.length]?.[0] ?? 'skeleton';
        spawns.push({
          room: i,
          wave: DUNGEON.wavesPerRoom - 1,
          kind,
          tier: 2,
          elite: true,
          q: at.q,
          r: at.r,
          ...(pair > 1 ? { hpBp: SIMA_SIGN.pair.hpBp } : {}),
        });
      }
    }
  }
  return spawns;
};

const zone = (s: BattleState, haz: SimaHazard, room: number, x: number, y: number, radius: number, o: Partial<Zone> = {}): Zone => {
  const z: Zone = {
    id: s.nextId++,
    kind: 'sima',
    haz,
    room,
    x,
    y,
    radius,
    cells: [],
    ticks: FOREVER,
    every: 0,
    damage: 0,
    slowBp: 0,
    pid: '',
    ...o,
  };
  s.zones.push(z);
  return z;
};

/** The stratum's hazards in the fighting room (a few) and the hazard room (more), never by a door or on a puzzle. */
const placeHazards = (s: BattleState, sm: SimaRun, L: DungeonLayout, rng: RngState, avoid: Hex[]): void => {
  const haz = STRATUM[sm.stratum].hazard;
  if (sm.stratum === 'marrow') {
    placeCryptTraps(s, (room) => room.kind === 'room' && !room.puzzle, false, avoid);
    return;
  }
  if (!haz) return;
  let beat = 0;
  const dmg = (n: number): number => mulBp(n, simaDmgBp(sm.floor));
  for (const i of [1, 2]) {
    const room = L.rooms[i];
    if (!room || room.puzzle) continue;
    const doors = [L.doors[i - 1], L.doors[i]].filter((d): d is Hex => !!d);
    const free = roomCells(room).filter((h) => !doors.some((d) => hexDist(h, d) < 2) && !avoid.some((a) => hexDist(h, a) < 2));
    const pick = (): Hex => free.splice(rngInt(rng, free.length), 1)[0] ?? { q: room.q, r: room.r };
    const per = (n: number): number => (i === 1 ? imax(1, n - 1) : n + 1);
    const c = W(room);
    const phase = (every: number): number => (beat++ * 17) % imax(1, every);
    if (haz === 'niche') {
      // Niches stand on the rim, in the walls.
      const rim = hexRing(room.radius)
        .map((h) => ({ q: room.q + h.q, r: room.r + h.r }))
        .filter((h) => !doors.some((d) => hexDist(h, d) < 2));
      const H = SIMA_HAZARD.niche;
      for (let k = 0; k < per(H.perRoom) && rim.length; k++) {
        const h = rim.splice(rngInt(rng, rim.length), 1)[0] as Hex;
        const w = W(h);
        zone(s, 'niche', i, w.x, w.y, 600, {
          every: H.every,
          phase: phase(H.every),
        });
      }
    } else if (haz === 'deep') {
      // Shallow water over the room, and the dark deep pools the leeches come out of.
      zone(s, 'water', i, c.x, c.y, room.radius * 1000 + 400, {
        slowBp: SIMA_HAZARD.water.slowBp,
      });
      const H = SIMA_HAZARD.deep;
      for (let k = 0; k < per(H.perRoom); k++) {
        const w = W(pick());
        zone(s, 'deep', i, w.x, w.y, H.radius, {
          every: H.every,
          phase: phase(H.every),
        });
      }
    } else if (haz === 'roots' || haz === 'hammer' || haz === 'rockfall') {
      const H = SIMA_HAZARD[haz];
      for (let k = 0; k < per(H.perRoom); k++) {
        const w = W(pick());
        zone(s, haz, i, w.x, w.y, H.radius, {
          every: H.every,
          phase: phase(H.every),
          damage: dmg(H.damage),
        });
      }
    } else if (haz === 'spores') {
      const H = SIMA_HAZARD.spores;
      for (let k = 0; k < per(H.perRoom); k++) {
        const w = W(pick());
        const a = rngInt(rng, 6);
        const d = hexRing(1)[a] as Hex;
        const v = W(d);
        zone(s, 'spores', i, w.x, w.y, H.radius, {
          vx: idiv(v.x * H.speed, 1000),
          vy: idiv(v.y * H.speed, 1000),
        });
      }
    } else if (haz === 'rime') {
      const H = SIMA_HAZARD.rime;
      for (let k = 0; k < per(H.perRoom); k++) {
        const w = W(pick());
        zone(s, 'rime', i, w.x, w.y, H.radius, { slowBp: H.slowBp });
      }
    } else if (haz === 'beam') {
      // Beams between two crystals on opposite sides of the room.
      const H = SIMA_HAZARD.beam;
      const rim = hexRing(room.radius - 1).map((h) => ({
        q: room.q + h.q,
        r: room.r + h.r,
      }));
      for (let k = 0; k < per(H.perRoom); k++) {
        const a = rngInt(rng, rim.length);
        const from = rim[a] as Hex;
        const to = rim[(a + idiv(rim.length, 2)) % rim.length] as Hex;
        const p = W(from);
        const q = W(to);
        zone(s, 'beam', i, p.x, p.y, H.width, {
          tx: q.x,
          ty: q.y,
          every: H.every,
          phase: phase(H.every),
          damage: dmg(H.damage),
        });
      }
    } else if (haz === 'ink') {
      const H = SIMA_HAZARD.ink;
      for (let k = 0; k < per(H.perRoom); k++) {
        const w = W(pick());
        zone(s, 'ink', i, w.x, w.y, H.start);
      }
    } else if (haz === 'web') {
      const H = SIMA_HAZARD.web;
      for (let k = 0; k < per(H.perRoom); k++) {
        const w = W(pick());
        zone(s, 'web', i, w.x, w.y, H.radius, { slowBp: H.slowBp });
      }
    }
  }
};

/**
 * Lays floor `floor` in the slot the party is not on: a new layout (`map.dungeon` replaced, never mutated: the battle's
 * params keep the first), its waves, its hazards and maybe a puzzle in the hazard room; the party stands in its entrance.
 */
export const layFloor = (s: BattleState, floor: number): void => {
  const d = s.dungeon;
  const sm = d?.sima;
  if (!d || !sm) return;
  const info = simaFloor(sm.weekSeed, sm.order, floor);
  const slot = sm.rev === 0 ? 0 : 1 - sm.slot;
  sm.floor = floor;
  sm.stratum = info.stratum;
  sm.band = info.band;
  sm.signs = info.signs;
  if (info.ward) sm.ward = info.ward;
  else delete sm.ward;
  sm.threshold = info.threshold;
  sm.slot = slot;
  sm.rev++;
  sm.stairs = null;
  sm.exit = null;
  sm.stairsT = 0;
  sm.exitT = 0;
  sm.boss = 0;
  sm.act = null;
  sm.props = [];
  const seed = deriveSeed(s.seed, 50000 + floor);
  const rng = seedRng(seed ^ 0x51a4);
  const L = fitChain(seed, slot);
  // A puzzle in the hazard room now and then (from the third floor; never the twin levers for one hero alone).
  if (floor >= 3 && rngChance(rng, 3300)) {
    const kind = drawPuzzles(rng, sm.party)[0];
    const room = L.rooms[2];
    if (kind && room) room.puzzle = kind;
  }
  s.map = { ...s.map, dungeon: L };
  s._cache = undefined;
  // What the floor above left behind goes with it.
  s.zones = s.zones.filter((z) => z.kind !== 'sima' && z.kind !== 'lava' && z.kind !== 'vent' && z.kind !== 'eruption' && z.kind !== 'glass' && z.kind !== 'shards');
  s.enemies = [];
  s.projectiles = [];
  d.room = -1;
  d.wave = 0;
  d.nextAt = 0;
  d.cleared = L.rooms.map((r) => r.kind === 'entry');
  d.marks = [];
  d.pools = [];
  d.boss = 0;
  delete d.joining;
  delete d.joinUntil;
  d.puzzles = [];
  L.rooms.forEach((r, i) => {
    if (r.puzzle) d.puzzles?.push(makePuzzle(rng, L, i, r.puzzle, sm.party));
  });
  d.spawns = floorSpawns(sm, L, rng);
  placeHazards(
    s,
    sm,
    L,
    rng,
    d.puzzles.flatMap((p) => p.spots),
  );
  touchGrid(s);
  // Into the entrance, standing (whoever fell above gets up on the stairs).
  const entry = L.rooms[0] as DungeonRoom;
  const c = W(entry);
  s.heroes.forEach((h, k) => {
    if (h.downTicks > 0) {
      h.downTicks = 0;
      h.revive = 0;
      h.hp = imax(1, mulBp(h.stats.maxHp, DUNGEON.clearReviveHpBp));
      h.anim = 'idle';
      ev(s, { t: 'up', id: h.id });
    }
    h.x = h.destX = h.ax = c.x + ((k % 3) - 1) * 600;
    h.y = h.destY = h.ay = c.y + (k > 5 ? -600 : k > 2 ? 600 : 0);
    h.moving = false;
    h.path = [];
    h.target = 0;
    h.held = 0;
    h.chillN = 0;
    h.stun = 0;
    h.sleep = 0;
  });
  ev(s, { t: 'skill', hero: 0, skill: 'simaFloor', x: c.x, y: c.y });
};

/** What the campaign hands a Chasm run: where it starts, the week, what the era allows and how the party scales. */
export interface SimaParams {
  start: number;
  weekSeed: number;
  allowed: (NightmareType | SimaCreature)[];
  /** Nightmares' count and health (bp, the party's size in), and the guardians' health before the floor. */
  countBp: number;
  hpBp: number;
  guardianHp: number;
}

/** Sets the Chasm up at battle creation and lays the run's first floor. */
export const startSima = (s: BattleState, p: SimaParams): void => {
  const d = s.dungeon;
  if (!d) return;
  d.dmgBp = 10000;
  d.sima = {
    floor: p.start,
    start: p.start,
    done: 0,
    stratum: 'ossuary',
    band: 'shadow',
    signs: [],
    order: simaOrder(p.weekSeed),
    weekSeed: p.weekSeed,
    slot: 0,
    rev: 0,
    threshold: false,
    stairs: null,
    exit: null,
    stairsT: 0,
    exitT: 0,
    left: false,
    boss: 0,
    act: null,
    props: [],
    party: s.heroes.length || 1,
    countBp: p.countBp,
    hpBp: p.hpBp,
    dmgBp: SIMA.baseDmgBp,
    guardianHp: p.guardianHp,
    allowed: p.allowed.slice().sort(),
    thresholds: [],
  };
  layFloor(s, p.start);
};

/**
 * Everything that comes out down here: the floor's health and blows, and the signs that touch it. Called for every
 * nightmare spawned in the Chasm (waves, reinforcements, split halves, brood): `given` health was set by its maker.
 */
export const simaSpawned = (s: BattleState, e: Enemy, given: boolean): void => {
  const sm = s.dungeon?.sima;
  if (!sm || e.kind === 'boneWall') return;
  e.damage = mulBp(mulBp(e.damage, sm.dmgBp), simaDmgBp(sm.floor));
  if (!given) e.hp = e.maxHp = imax(1, mulBp(mulBp(e.maxHp, sm.hpBp), simaHpBp(sm.floor)));
};

/** A Chasm nightmare's blow on a hero: rage below half its health, frost that chills. */
export const simaBlow = (s: BattleState, h: Hero, from: Enemy, amount: number): number => {
  const sm = s.dungeon?.sima;
  if (!sm) return amount;
  if (sm.signs.includes('frost')) chill(s, h, SIMA_SIGN.frost.stacks);
  if (sm.signs.includes('rage') && from.hp < mulBp(from.maxHp, SIMA_SIGN.rage.atBp)) return mulBp(amount, SIMA_SIGN.rage.dmgBp);
  return amount;
};

/** A blow on a Chasm nightmare: the ward sign's type, molten metal frozen brittle. */
export const simaTakes = (s: BattleState, e: Enemy, dmg: number, type: DamageType): number => {
  const sm = s.dungeon?.sima;
  if (!sm) return dmg;
  if (sm.ward === type && e.kind !== 'crystal' && e.kind !== 'cocoon' && e.kind !== 'iceBlock') dmg = mulBp(dmg, 10000 - SIMA_SIGN.ward.resistBp);
  if (e.kind === 'molten') {
    const M = SIMA_CREATURE.molten;
    if (type === 'frost') e.brittle = s.tick + M.brittleTicks;
    if ((e.brittle ?? 0) > s.tick) dmg = mulBp(dmg, M.brittleTakenBp);
  }
  return dmg;
};

/** A hero chilled by rime or a frosty blow: enough stacks freeze it a moment. */
export const chill = (s: BattleState, h: Hero, n: number): void => {
  if (h.downTicks > 0) return;
  const H = SIMA_HAZARD.rime;
  if (s.tick - (h.chillAt ?? -1000) > 24) h.chillN = 0;
  h.chillN = (h.chillN ?? 0) + n;
  h.chillAt = s.tick;
  if ((h.chillN ?? 0) >= H.stacks) {
    h.chillN = 0;
    h.stun = imax(h.stun, H.freezeTicks);
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'simaFreeze' });
  }
};

/** Roots (or a rootwight's blow) hold a hero: it strikes and casts but can't walk; not again for a moment after. */
export const hold = (s: BattleState, h: Hero, ticks: number): void => {
  if (h.downTicks > 0 || (h.frenzy ?? 0) > 0) return;
  if ((h.held ?? 0) <= 0 && s.tick - (h.heldAt ?? -100000) < ticks + 36) return;
  h.held = imax(h.held ?? 0, ticks);
  h.heldAt = s.tick;
  h.moving = false;
  h.path = [];
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'simaHeld' });
};

/** Sleeping spores: down for a moment unless it woke from them lately. */
const spore = (s: BattleState, h: Hero, ticks: number, immune: number): void => {
  if (h.downTicks > 0 || (h.frenzy ?? 0) > 0 || (h.sleep ?? 0) > 0 || s.tick < (h.sporeAt ?? -1)) return;
  h.sleep = ticks;
  h.sporeAt = s.tick + ticks + immune;
  h.moving = false;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 600, kind: 'simaSleep' });
};

/** How fast a hero walks down here (bp): water and webs slow it, roots hold it. */
export const simaSpeedBp = (s: BattleState, h: Hero): number => {
  if ((h.held ?? 0) > 0) return 0;
  let bp = 10000;
  for (const z of s.zones) {
    if (z.kind !== 'sima' || z.slowBp <= 0 || !withinRange(z.x, z.y, h.x, h.y, z.radius)) continue;
    const take = 10000 - z.slowBp;
    if (take < bp) bp = take;
  }
  return bp;
};

/** Distance² from a point to the segment a→b (fixed units). */
const segDist2 = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return dist2(px, py, ax, ay);
  let t = idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2);
  t = imax(0, imin(1000, t));
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000));
};
export const onSegment = (px: number, py: number, ax: number, ay: number, bx: number, by: number, width: number): boolean => segDist2(px, py, ax, ay, bx, by) <= width * width;

/** A marked strike lands: what it does to whoever is in it. */
const strike = (s: BattleState, z: Zone): void => {
  const up = s.heroes.filter(standing);
  const sm = s.dungeon?.sima;
  if (z.haz === 'beam') {
    for (const h of up) if (onSegment(h.x, h.y, z.x, z.y, z.tx ?? z.x, z.ty ?? z.y, z.radius)) damageHero(s, h, z.damage, null, 'lightning');
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'simaBeam' });
    return;
  }
  const inside = up.filter((h) => withinRange(z.x, z.y, h.x, h.y, z.radius));
  if (z.haz === 'roots' || z.haz === 'quake') {
    for (const h of inside) {
      damageHero(s, h, z.damage, null, 'physical');
      if ((z.stun ?? 0) > 0) hold(s, h, z.stun as number);
    }
  } else if (z.haz === 'ink') {
    // A scribe's rune closes: whoever stands on it is cursed.
    const S = SIMA_CREATURE.scribe;
    for (const h of inside) {
      damageHero(s, h, sm ? mulBp(S.dmg, simaDmgBp(sm.floor)) : S.dmg, null, 'shadow');
      h.curse = imax(h.curse ?? 0, S.curseTicks);
      h.curseBp = S.healBp;
    }
  } else if (z.haz === 'hammer') {
    for (const h of inside) {
      damageHero(s, h, z.damage, null, 'physical');
      h.stun = imax(h.stun, SIMA_HAZARD.hammer.stun);
    }
  } else if (z.haz === 'rockfall') {
    for (const h of inside) damageHero(s, h, z.damage, null, 'physical');
    // Bone worms come out of the broken floor.
    if (sm) for (let k = 0; k < SIMA_HAZARD.rockfall.worms; k++) spawnHere(s, 'boneworm', z.x, z.y);
  } else if (z.haz === 'niche') {
    for (let k = 0; k < SIMA_HAZARD.niche.count; k++) spawnHere(s, 'skeleton', z.x + (k - 1) * 300, z.y);
  }
  ev(s, {
    t: 'boom',
    x: z.x,
    y: z.y,
    r: z.radius,
    kind: z.haz === 'niche' ? 'simaNiche' : 'simaStrike',
  });
};

/** A nightmare comes out of the floor (it carries the floor like any other). */
const spawnHere = (s: BattleState, kind: EnemyKind, x: number, y: number): Enemy => {
  return spawnEnemy(s, kind, 1, false, 0, x, y);
};

/** Each tick: the hazards of the floor, the conditions they leave, and the way down or out once the hall is done. */
export const simaTick = (s: BattleState, d: DungeonState): void => {
  const sm = d.sima;
  const L = s.map.dungeon;
  if (!sm || !L) return;
  const up = s.heroes.filter(standing);
  const fight = d.room >= 0;
  for (const h of s.heroes) if ((h.held ?? 0) > 0) h.held = (h.held ?? 0) - 1;
  const fresh: Zone[] = [];
  let menders: Enemy[] | undefined;
  for (const z of s.zones) {
    if (z.kind !== 'sima') continue;
    const here = z.room === d.room && fight;
    // Marked strikes: the ring counts down, then lands.
    if (z.warn !== undefined) {
      if (z.warn > 0) z.warn--;
      if (z.warn === 0) {
        strike(s, z);
        z.warn = -1;
        z.ticks = 6;
      }
      continue;
    }
    const H = z.haz;
    // Emitters beat only while their room is being fought.
    if ((H === 'niche' || H === 'roots' || H === 'hammer' || H === 'rockfall' || H === 'beam') && here && z.every > 0 && (s.tick + (z.phase ?? 0)) % z.every === 0) {
      const warn = H === 'niche' ? SIMA_HAZARD.niche.warn : SIMA_HAZARD[H].warn;
      fresh.push({
        ...z,
        id: 0,
        warn,
        ticks: warn + 8,
        every: 0,
        ...(H === 'roots' ? { stun: SIMA_HAZARD.roots.holdTicks } : {}),
      });
      continue;
    }
    if (H === 'deep' && here && (s.tick + (z.phase ?? 0)) % z.every === 0) {
      for (let k = 0; k < SIMA_HAZARD.deep.count; k++) spawnHere(s, 'leech', z.x + (k - 1) * 250, z.y + (k % 2) * 250);
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'simaDeep' });
      continue;
    }
    if (H === 'spores') {
      // The cloud drifts and turns back at its room's rim.
      const room = L.rooms[z.room ?? -1];
      if (room) {
        const c = W(room);
        const nx = z.x + (z.vx ?? 0);
        const ny = z.y + (z.vy ?? 0);
        const lim = (room.radius - 1) * 1000;
        if (dist2(nx, ny, c.x, c.y) > lim * lim) {
          z.vx = -(z.vx ?? 0);
          z.vy = -(z.vy ?? 0);
        } else {
          z.x = nx;
          z.y = ny;
        }
      }
      const P = SIMA_HAZARD.spores;
      for (const h of up) if (withinRange(z.x, z.y, h.x, h.y, z.radius)) spore(s, h, P.sleepTicks, P.immune);
      continue;
    }
    if (H === 'rime' || H === 'molten' || H === 'embers') {
      if (z.ticks % 12 === 0)
        for (const h of up) {
          if (!withinRange(z.x, z.y, h.x, h.y, z.radius)) continue;
          if (H === 'rime') chill(s, h, 1);
          else damageHero(s, h, z.damage, null, 'fire');
        }
      // The fire burns nightmares too: one that mends (the peat troll) can't while it stands in it, and a while after.
      // Every tick (a mending tick slips through otherwise), over the few that mend only.
      if (H !== 'rime') {
        menders ??= s.enemies.filter((e) => e.hp > 0 && NIGHTMARES[e.kind as NightmareType]?.regen);
        for (const e of menders) if (withinRange(z.x, z.y, e.x, e.y, z.radius)) e.scorch = s.tick;
      }
      continue;
    }
    if (H === 'ink') {
      const P = SIMA_HAZARD.ink;
      if (here && z.radius < P.max && s.tick % 12 === 0) z.radius += P.grow * 10;
      for (const h of up) {
        if (!withinRange(z.x, z.y, h.x, h.y, z.radius)) continue;
        h.curse = imax(h.curse ?? 0, P.curseTicks);
        h.curseBp = P.healBp;
      }
      continue;
    }
    if (H === 'web') {
      const P = SIMA_HAZARD.web;
      // Stepping on a web calls the brood (once in a while per web).
      if (here && s.tick >= (z.pulses ?? 0) && up.some((h) => withinRange(z.x, z.y, h.x, h.y, z.radius))) {
        z.pulses = s.tick + P.cooldown;
        for (let k = 0; k < P.calls; k++) spawnHere(s, 'spiderling', z.x + (k - 1) * 400, z.y);
        ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'simaWeb' });
      }
    }
  }
  for (const f of fresh) {
    f.id = s.nextId++;
    s.zones.push(f);
  }
  creatureTicks(s, sm);
  stairsTick(s, d, sm);
};

/** The Chasm's creatures that leave something behind them as they go. */
const creatureTicks = (s: BattleState, sm: SimaRun): void => {
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    if (e.kind === 'rime' && (s.tick - e.born) % SIMA_CREATURE.rime.every === 0) {
      const R = SIMA_CREATURE.rime;
      s.zones.push({
        id: s.nextId++,
        kind: 'sima',
        haz: 'rime',
        x: e.x,
        y: e.y,
        radius: R.radius,
        cells: [],
        ticks: R.ticks,
        every: 0,
        damage: 0,
        slowBp: SIMA_HAZARD.rime.slowBp,
        pid: '',
      });
    } else if (e.kind === 'molten' && e.anim === 'walk' && (s.tick - e.born) % SIMA_CREATURE.molten.every === 0) {
      // A trail where it walks: standing to fight, it leaves none under itself (a hero in melee isn't always in fire).
      const M = SIMA_CREATURE.molten;
      s.zones.push({
        id: s.nextId++,
        kind: 'sima',
        haz: 'molten',
        x: e.x,
        y: e.y,
        radius: M.radius,
        cells: [],
        ticks: M.ticks,
        every: 0,
        damage: mulBp(M.dmg, simaDmgBp(sm.floor)),
        slowBp: 0,
        pid: '',
      });
    } else if (e.kind === 'scribe' && (s.tick - e.born) % SIMA_CREATURE.scribe.every === SIMA_CREATURE.scribe.every - 1) {
      // A rune under the nearest hero in reach: whoever stands on it when it closes is cursed.
      const S = SIMA_CREATURE.scribe;
      let aim: Hero | null = null;
      let bd = S.range * S.range + 1;
      for (const h of s.heroes) {
        if (h.downTicks > 0) continue;
        const dd = dist2(e.x, e.y, h.x, h.y);
        if (dd < bd) {
          bd = dd;
          aim = h;
        }
      }
      if (!aim) continue;
      e.anim = 'cast';
      e.animT = 0;
      s.zones.push({
        id: s.nextId++,
        kind: 'sima',
        haz: 'ink',
        x: aim.x,
        y: aim.y,
        radius: S.radius,
        cells: [],
        ticks: S.warn + 8,
        every: 0,
        damage: 0,
        slowBp: 0,
        pid: '',
        warn: S.warn,
        stun: -1,
      });
    }
  }
};

/** Once the hall is done: everyone standing together on the stairs goes down; on the way out, they leave. */
const stairsTick = (s: BattleState, d: DungeonState, sm: SimaRun): void => {
  if (!sm.stairs || !sm.exit) return;
  const up = s.heroes.filter(standing);
  const live = up.filter((h) => h.control !== 'ghost');
  const deciders = live.length ? live : up;
  const all = (h: Hex): boolean => deciders.length > 0 && deciders.every((o) => !o.moving && withinRange(o.x, o.y, W(h).x, W(h).y, SIMA.stairsRange));
  sm.stairsT = all(sm.stairs) ? sm.stairsT + 1 : 0;
  sm.exitT = all(sm.exit) ? sm.exitT + 1 : 0;
  if (sm.exitT >= SIMA.chooseTicks) {
    sm.left = true;
    return;
  }
  if (sm.stairsT >= SIMA.chooseTicks) {
    const c = W(sm.stairs);
    ev(s, { t: 'skill', hero: 0, skill: 'descend', x: c.x, y: c.y });
    layFloor(s, sm.floor + 1);
  }
};

/** The hall is cleared: the floor counts (a guardian's, a piece into the chest), and the stairs and the way out open. */
export const simaHallCleared = (s: BattleState, d: DungeonState): void => {
  const sm = d.sima;
  const L = s.map.dungeon;
  if (!sm || !L) return;
  sm.done++;
  if (sm.threshold) sm.thresholds.push(sm.floor);
  sm.boss = 0;
  sm.act = null;
  // Whatever the guardian left standing goes with it.
  for (const e of s.enemies) if (e.hp > 0 && (e.kind === 'crystal' || e.kind === 'cocoon' || e.kind === 'iceBlock')) e.hp = 0;
  const hall = L.rooms[L.rooms.length - 1] as DungeonRoom;
  const ring = hexRing(2).map((h) => ({ q: hall.q + h.q, r: hall.r + h.r }));
  sm.stairs = ring[0] as Hex;
  sm.exit = ring[idiv(ring.length, 2)] as Hex;
  const c = W(sm.stairs);
  ev(s, { t: 'skill', hero: 0, skill: 'stairsOpen', x: c.x, y: c.y });
};

/** A Chasm nightmare falls: the echo brings it back once as a shadow, embers burn where it fell, a puffball bursts. */
export const simaDeath = (s: BattleState, e: Enemy): void => {
  const sm = s.dungeon?.sima;
  if (!sm) return;
  if (e.kind === 'puffball') {
    const P = SIMA_CREATURE.puffball;
    for (const h of s.heroes) if (standing(h) && withinRange(e.x, e.y, h.x, h.y, P.radius)) spore(s, h, P.sleepTicks, P.immune);
    ev(s, { t: 'boom', x: e.x, y: e.y, r: P.radius, kind: 'simaSpores' });
  }
  const inert = e.kind === 'crystal' || e.kind === 'cocoon' || e.kind === 'iceBlock' || e.kind === 'boneWall';
  if (inert || e.id === sm.boss) return;
  if (sm.signs.includes('embers')) {
    const E = SIMA_SIGN.embers;
    s.zones.push({
      id: s.nextId++,
      kind: 'sima',
      haz: 'embers',
      x: e.x,
      y: e.y,
      radius: E.radius,
      cells: [],
      ticks: E.ticks,
      every: 0,
      damage: mulBp(E.dmg, simaDmgBp(sm.floor)),
      slowBp: 0,
      pid: '',
    });
  }
  if (sm.signs.includes('echo') && !e.echo && !e.split && !e.elite) {
    const back = spawnEnemy(s, e.kind, e.tier, false, e.sector, e.x, e.y, imax(1, mulBp(e.maxHp, SIMA_SIGN.echo.hpBp)));
    back.echo = true;
    if (e.room !== undefined) back.room = e.room;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 600, kind: 'simaEcho' });
  }
};

/** Wether the glass cracks here: the Chasm's glass stratum, outside its entrance and any puzzle. */
export const simaOnGlass = (s: BattleState, h: Hero): boolean => {
  const sm = s.dungeon?.sima;
  const L = s.map.dungeon;
  if (!sm || !L || sm.stratum !== 'glass') return false;
  return L.rooms.some((r) => r.kind !== 'entry' && !r.puzzle && withinRange(W(r).x, W(r).y, h.x, h.y, r.radius * 1000 + 300));
};

/** The deepest a hall's guardian reaches into: how far from the hall's centre (for props). */
export const hallOf = (s: BattleState): DungeonRoom | null => {
  const L = s.map.dungeon;
  return L ? (L.rooms[L.rooms.length - 1] as DungeonRoom) : null;
};

/** For tests and the balance tool: the slot a floor sits in never strays out of the map. */
export const simaFits = (L: DungeonLayout): boolean => L.floor.every((h) => hexLen(h.q, h.r) <= SIMA_RADIUS - 2);

/** Re-exported for the campaign: the heroes' distance helper in fixed units. */
export const simaDist = (a: { x: number; y: number }, b: { x: number; y: number }): number => isqrt(dist2(a.x, a.y, b.x, b.y));
